import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WikiTreeNode } from '@sd/shared';
import { api } from '../lib/api.js';
import { DvIcon, WindMark } from '../components/DvIcon.js';
import { WORLD } from '../lib/world.js';

function flattenCounts(roots: WikiTreeNode[]): Map<string, number> {
  const map = new Map<string, number>();
  function visit(n: WikiTreeNode): number {
    let total = n.items.length;
    for (const c of n.children) total += visit(c);
    const key = n.path.toLowerCase();
    map.set(key, (map.get(key) ?? 0) + total);
    return total;
  }
  for (const r of roots) visit(r);
  return map;
}

/** 8 кафедральних плиток home — як у прототипі (renderHomeTiles). */
const HOME_TILES: Array<{ label: string; icon: string; color: string; path: string }> = [
  { label: 'Міста', icon: 'city', color: 'cat-city', path: 'World/Cities' },
  { label: 'Бестіарій', icon: 'beast', color: 'danger', path: 'Characters/Bestiary' },
  { label: 'Локації', icon: 'location', color: 'cat-location', path: 'World/Locations' },
  { label: 'Предмети', icon: 'item', color: 'cat-item', path: 'Items' },
  { label: 'Квести', icon: 'quest', color: 'cat-quest', path: 'Quests' },
  { label: 'Вміння', icon: 'ability', color: 'cat-ability', path: 'Abilities' },
  { label: 'Фракції', icon: 'faction', color: 'cat-faction', path: 'Factions' },
  { label: 'Персонажі', icon: 'character', color: 'cat-character', path: 'Characters' },
];

/** Домашня сторінка світу: герой + картки розділів (1:1 з прототипом). */
export function WikiIndex(): JSX.Element {
  const [counts, setCounts] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    api.wikiTree().then((r) => setCounts(flattenCounts(r.tree))).catch(() => {});
  }, []);

  return (
    <div className="dv-home">
      <section className="dv-hero">
        <WindMark className="dv-hero-wind" size={260} stroke={1.1} color="currentColor" />
        <div className="dv-hero-body">
          <p className="dv-hero-eyebrow">Світ-довідник · світла героїка</p>
          <h1 className="dv-hero-title">{WORLD.brandName}</h1>
          <p className="dv-hero-lede">
            Жива енциклопедія авторського світу на правилах Daggerheart і помічник
            Майстра у грі: кожна сутність — це <b>стат-картка</b> механіки й <b>опис</b> лору.
          </p>
          <div className="dv-hero-actions">
            <Link to="/wiki/_/Characters/Bestiary" className="dv-btn dv-btn--primary">
              <DvIcon name="beast" size={17} /> Відкрити бестіарій
            </Link>
            <Link to="/battle" className="dv-btn dv-btn--ghost">
              <DvIcon name="dice" size={17} color="var(--hope-deep)" /> Інструменти бою
            </Link>
          </div>
        </div>
      </section>

      <div className="dv-section-label">Розділи світу</div>
      <div className="dv-card-grid">
        {HOME_TILES.map((t) => {
          const total = counts.get(t.path.toLowerCase()) ?? 0;
          const accent = `var(--${t.color})`;
          return (
            <Link key={t.path} to={`/wiki/_/${t.path}`} className="dv-cat-card">
              <span className="dv-cat-ico" style={{ background: `color-mix(in srgb, ${accent} 15%, transparent)`, color: accent }}>
                <DvIcon name={t.icon} size={23} />
              </span>
              <div>
                <div className="dv-cat-name">{t.label}</div>
                <div className="dv-cat-meta">{total} записів</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
